// src/app/app.config.ts

import { ApplicationConfig } from '@angular/core';
import {
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  provideRouter,
  withRouterConfig,
  withEnabledBlockingInitialNavigation,
} from '@angular/router';
import { routes } from './app.routes';
import { environment } from '@env/environment';
import { TitleStrategy } from '@angular/router';
import { AppTitleStrategy } from '@seo/app-title.strategy';
import { provideSeoRouting } from '@core/services/seo.providers';

function isServerRuntime(): boolean {
  // Angular SSR typically sets this global in the server bundle.
  return (
    (globalThis as unknown as { ngServerMode?: boolean }).ngServerMode === true
  );
}

function browserHasHydrationMarkers(): boolean {
  if (typeof document === 'undefined') return false;

  // Angular hydration adds `ngh` attributes into the rendered HTML.
  // If we’re on a CSR shell (SWA fallback index.html), this will be absent.
  return document.querySelector('[ngh]') !== null;
}

const server = isServerRuntime();
const enableHydrationOnBrowser =
  environment.enableHydration && browserHasHydrationMarkers();

// On the server: ALWAYS include hydration so prerender/SSR emits the serialized info.
// On the browser: only attempt hydration when (a) env allows it and (b) markers exist.
const hydrationProviders = server
  ? [provideClientHydration()]
  : enableHydrationOnBrowser
    ? [provideClientHydration(withEventReplay())]
    : [];

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),

    // SEO/SSR: block initial navigation so prerender waits for routing + async work
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({ onSameUrlNavigation: 'reload' }),
    ),

    ...hydrationProviders,

    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideSeoRouting(),
  ],
};
